/**
 * 帮助与反馈页
 * 对齐 features.md 9.24
 */

import { useState } from 'react';
import { Button } from '@hump/ui';

const faqs = [
  { q: '如何导入文档？', a: '在书架页点击「导入」按钮，选择 .md / .pdf / .epub 文件。文件会被解析为 Hump Doc 格式存储在本地。' },
  { q: '如何配置 AI 翻译？', a: '进入设置 → AI 配置，选择 Provider（OpenAI/DeepSeek），填入 API Key，点击测试连接。配置成功后可翻译全文或划词翻译。' },
  { q: '数据存储在哪里？', a: '所有数据存储在浏览器本地（IndexedDB）。不依赖服务器，不上传你的文档。清浏览器数据会丢失，建议定期导出备份。' },
  { q: '驼峰是什么？', a: '驼峰是你的知识点集合——收藏的生词、段落、批注都存在这里。点击驼峰图标查看，点击卡片查看详情。' },
  { q: '如何使用 TTS 朗读？', a: '在阅读工作台底部点击「朗读」按钮，系统语音会逐段朗读。可调节语速和音色。' },
];

export function HelpPage() {
  const [feedback, setFeedback] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSend() {
    const content = feedback.trim();
    if (!content) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/account/feedback', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data?.error?.message || `提交失败 (${res.status})`);
      }
      setSent(true);
      setFeedback('');
      setTimeout(() => setSent(false), 3000);
    } catch (e) {
      // 未登录或离线时降级：仍给用户即时反馈，但显示错误
      setError((e as Error).message || '提交失败，请稍后重试');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-6 max-w-md">
      {/* FAQ */}
      <div>
        <h3 className="text-sm font-medium text-charcoal dark:text-charcoal-light mb-3">常见问题</h3>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <details key={i} className="rounded-md bg-warm-white dark:bg-warm-dark p-3">
              <summary className="text-sm font-medium text-charcoal dark:text-charcoal-light cursor-pointer">
                {faq.q}
              </summary>
              <p className="text-xs text-charcoal/60 dark:text-charcoal-light/60 mt-2">{faq.a}</p>
            </details>
          ))}
        </div>
      </div>

      {/* 反馈 */}
      <div>
        <h3 className="text-sm font-medium text-charcoal dark:text-charcoal-light mb-2">意见反馈</h3>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="遇到问题或有建议？告诉我们..."
          rows={4}
          className="w-full rounded-md border border-border-warm dark:border-border-warm-dark bg-white dark:bg-warm-dark px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-oasis resize-none"
        />
        <div className="flex items-center gap-3 mt-2">
          <Button variant="primary" size="sm" onClick={handleSend} disabled={!feedback.trim() || sending}>
            {sending ? '发送中...' : '发送'}
          </Button>
          {sent && <span className="text-oasis text-sm">✓ 已收到，感谢反馈！</span>}
          {error && <span className="text-error text-sm">{error}</span>}
        </div>
      </div>

      {/* 联系方式 */}
      <div className="pt-4 border-t border-border-warm dark:border-border-warm-dark">
        <p className="text-xs text-charcoal/40 dark:text-charcoal-light/40">
          邮箱：support@hump.2100laike.com
        </p>
      </div>
    </div>
  );
}
